"use client";
import React, { useState, useEffect } from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight, Edit2 } from "react-icons/md";
import { FaEye, FaPencilAlt, FaCheck, FaTimes } from "react-icons/fa";
import { ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { useTimesheets } from "../../context/TimesheetContext";
import { CDN_URL, UPLOAD_URL } from "../../config/apiConfig";
import { useEmployees } from "../../context/EmployeeContext";
import { useProjects } from "../../context/ProjectContext";
import { useAuth } from "../../context/AuthContext";

export default function TimesheetsPage() {
  const { timesheets, updateTimesheetStatus, createTimesheet, updateTimesheet } = useTimesheets();
  const { employees } = useEmployees();
  const { projects } = useProjects();
  const { canDo } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [selected, setSelected] = useState(null); // for modal
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = timesheets.filter(
    (item) =>
      `${item.first_name || ""} ${item.last_name || ""}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      item.project_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.id?.toString().toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Toggle status manually
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Approved" ? "Pending" : "Approved";
    await updateTimesheetStatus(id, newStatus);
  };

  const [addForm, setAddForm] = useState({
    check_in: "",
    check_out: "",
    working_hours: "",
  });

  const calculateHours = (start, end) => {
    if (!start || !end) return "";

    const startTime = new Date(`1970-01-01T${start}:00`);
    const endTime = new Date(`1970-01-01T${end}:00`);

    let diff = (endTime - startTime) / (1000 * 60); // minutes

    if (diff < 0) diff += 24 * 60;

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const [editForm, setEditForm] = useState({
    check_in: "",
    check_out: "",
    working_hours: "",
  });

  useEffect(() => {
    if (editing) {
      setEditForm({
        check_in: editing.check_in || "",
        check_out: editing.check_out || "",
        working_hours: editing.working_hours || "",
      });
    }
  }, [editing]);

  // 🧩 Add Timesheet submit
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    // ✅ Collect all values directly from form fields
    const emp_id = form.emp_id.value.trim();
    const project_id = form.project_id.value.trim();
    const rawDate = form.date.value;
    const check_in = form.check_in.value;
    const check_out = form.check_out.value;
    const working_hours = addForm.working_hours;
    const overtime = form.overtime.value || "0";
    const status = form.status.value || "Pending";

    // ✅ Convert date to DD-MM-YYYY format
    const formattedDate = new Date(rawDate)
      .toLocaleDateString("en-GB")
      .replace(/\//g, "-");

    // ✅ Create JSON payload
    const payload = {
      emp_id,
      project_id,
      date: formattedDate,
      check_in,
      check_out,
      working_hours,
      overtime,
      status,
    };

    try {
      await createTimesheet(payload);

      form.reset();

      // Reset local calculated-fields state
      setAddForm({
        check_in: "",
        check_out: "",
        working_hours: "",
      });

      setAddModal(false);
    } catch (err) {
      console.error("❌ Failed to add timesheet:", err);
    }
  };

  // ✏️ Edit Timesheet submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editing) return;

    const form = e.target;

    const emp_id = form.emp_id.value.trim();
    const project_id = form.project_id.value.trim();
    const rawDate = form.date.value; // YYYY-MM-DD from input
    const check_in = editForm.check_in;
    const check_out = editForm.check_out;
    const working_hours = editForm.working_hours;
    const overtime = form.overtime.value || "0";
    const status = form.status.value || "Pending";

    // Convert date to DD-MM-YYYY to match your table format
    const formattedDate = rawDate ? rawDate.split("-").reverse().join("-") : "";

    const payload = {
      emp_id,
      project_id,
      date: formattedDate,
      check_in,
      check_out,
      working_hours,
      overtime,
      status,
      update_audit_id: JSON.parse(localStorage.getItem("user") || "{}").id,
    };

    try {
      // Use your context API function
      await updateTimesheet(editing.id, payload);
      setEditModal(false); // close modal
      console.log("Timesheet updated successfully!");
    } catch (err) {
      console.error("🔥 Error updating timesheet:", err);
      alert(`Update failed: ${err.message}`);
    }
  };

  // Approve from modal
  const approveTimesheet = async (id) => {
    await updateTimesheetStatus(id, "Approved");
    setSelected(null);
  };

  const approveAllTimesheets = async () => {
    try {
      const pendingItems = paginated.filter(
        (item) => item.status !== "Approved"
      );

      await Promise.all(
        pendingItems.map((item) =>
          updateTimesheetStatus(item.id, "Approved")
        )
      );

      console.log("✅ All timesheets approved");
    } catch (err) {
      console.error("❌ Failed to approve all:", err);
    }
  };

  // 🔍 TEMP DEBUG: check what the status field actually looks like
  useEffect(() => {
    console.log("employees sample:", employees.slice(0, 5).map(e => ({
      name: `${e.first_name} ${e.last_name}`,
      status: e.status,
    })));
  }, [employees]);

  return (
    <main className="p-6 min-h-screen bg-[#f3f8ff] overflow-y-auto max-md:ml-0 transition-all duration-300 font-nunito">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Time-sheets</h1>

      <div className="bg-white rounded-xl shadow-md p-5">
        {/* Toolbar: Search left, Buttons right */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mb-5">
          {/* LEFT: Search */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search..."
                className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-md bg-white outline-none transition-all w-full focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}
          </div>

          {/* RIGHT: Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {canDo("Timesheet", "View") && (
              <button
                onClick={approveAllTimesheets}
                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white text-sm font-medium px-4 py-2 rounded-md shadow-sm transition-all duration-150"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Approve All
              </button>
            )}

            {canDo("Timesheet", "Add") && (
              <button
                onClick={() => setAddModal(true)}
                className="inline-flex items-center gap-1.5 active:scale-95 text-white text-sm font-medium px-4 py-2 rounded-md shadow-sm transition-all duration-150"
                style={{
                  background: "linear-gradient(135deg, #2563eb, #d12465)",
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Timesheet
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl">
          <table className="min-w-full text-sm text-left table-fixed">
            <thead className="bg-[#f3f8ff] text-gray-700">
              <tr>
                <th className="p-3 font-semibold w-16">Id</th>
                <th className="p-3 font-semibold w-32">Submitted Date</th>
                <th className="p-3 font-semibold w-48">Employee Name</th>
                <th className="p-3 font-semibold w-40">Project Title</th>
                <th className="p-3 font-semibold w-24">Date</th>
                <th className="p-3 font-semibold w-24">Start Time</th>
                <th className="p-3 font-semibold w-24">End Time</th>
                <th className="p-3 font-semibold w-28">Worked Hours</th>
                <th className="p-3 font-semibold w-24">Over-time</th>
                <th className="p-3 font-semibold w-24">Status</th>
                {(canDo("Timesheet", "View") || canDo("Timesheet", "Edit")) && (
                  <th className="p-3 font-semibold text-center w-32">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginated.map((item, i) => (
                <tr
                  key={i}
                  className={`border-t ${
                    i % 2 === 1 ? "bg-purple-50" : "bg-white"
                  } hover:bg-gray-50 transition-colors`}
                >
                  <td className="p-3 text-purple-600 font-mono w-16">
                    {(i + 1).toString().padStart(2, "0")}
                  </td>
                  <td className="p-3 w-32 truncate" title={item.create_audit_time}>
                    {item.create_audit_time}
                  </td>
                  <td className="p-3 w-48">
                    <div className="flex items-center gap-3">
                      <img
                        src={`${CDN_URL}/${item.profile}`}
                        alt={item.first_name}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                      <span className="truncate" title={`${item.first_name} ${item.last_name}`}>
                        {item.first_name} {item.last_name}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 w-40 truncate" title={item.project_title}>
                    {item.project_title}
                  </td>
                  <td className="p-3 w-24">{item.date}</td>
                  <td className="p-3 w-24">{item.check_in}</td>
                  <td className="p-3 w-24">{item.check_out}</td>
                  <td className="p-3 w-28">{item.working_hours} hrs</td>
                  <td className="p-3 w-24">{item.overtime}</td>

                  {/* ✅ Status */}
                  <td className="p-3 w-24">
                    <button
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors w-full
                        ${
                          item.status === "Approved"
                            ? "bg-green-100 text-green-700"
                            : item.status === "Rejected"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                    >
                      {item.status}
                    </button>
                  </td>

                  {/* ✅ Actions */}
                  <td className="p-3 text-center w-32">
                    <div className="flex items-center justify-center gap-2">
                      {/* View */}
                      {canDo("Timesheet", "View") && (
                        <button
                          onClick={() => setSelected(item)}
                          className="text-gray-600 hover:text-purple-600 transition-colors"
                          title="View Details"
                        >
                          <FaEye size={16} />
                        </button>
                      )}

                      {/* Edit */}
                      {canDo("Timesheet", "Edit") && (
                        <FaPencilAlt
                          onClick={() => {
                            setEditing(item);
                            setEditModal(true);
                          }}
                          className="text-gray-600 hover:text-purple-600 cursor-pointer transition-colors"
                          size={16}
                          title="Edit Timesheet"
                        />
                      )}

                      {/* ✅ Approve */}
                      {canDo("Timesheet", "View") && item.status !== "Approved" && (
                        <button
                          onClick={() => updateTimesheetStatus(item.id, "Approved")}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Approve"
                        >
                          <FaCheck size={16} />
                        </button>
                      )}

                      {/* ❌ Reject */}
                      {canDo("Timesheet", "View") && item.status !== "Rejected" && (
                        <button
                          onClick={() => updateTimesheetStatus(item.id, "Rejected")}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Reject"
                        >
                          <FaTimes size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {paginated.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-4 text-center text-gray-500 italic">
                    No matching records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
          {/* Rows per page */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Rows:</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
              value={rowsPerPage}
              onChange={(e) => {
                setPage(1);
                setRowsPerPage(Number(e.target.value));
              }}
            >
              {[5, 10, 20].map((num) => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          {/* Pagination controls */}
          <div className="flex items-center gap-1 flex-wrap justify-center">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsLeft size={20} />
            </button>
            {/* Prev */}
            <button
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
            >
              <MdKeyboardArrowLeft size={18} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => {
              const pageNum = i + 1;

              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                Math.abs(page - pageNum) <= 1
              ) {
                return (
                  <button
                    key={i}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      page === pageNum
                        ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              }

              if (pageNum === page - 2 || pageNum === page + 2) {
                return <span key={i} className="px-1 text-gray-400">...</span>;
              }

              return null;
            })}

            {/* Next */}
            <button
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
            >
              <MdKeyboardArrowRight size={18} />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* ✅ View Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[480px] relative font-nunito">
            <button
              className="absolute top-2 right-3 text-gray-500 hover:text-gray-700 text-xl"
              onClick={() => setSelected(null)}
            >
              ×
            </button>

            <h2 className="text-xl font-semibold mb-4 text-purple-700">
              Timesheet Details
            </h2>

            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <p><strong>ID:</strong> {selected.id}</p>
              <p><strong>Name:</strong> {selected.first_name} {selected.last_name}</p>
              <p><strong>Project:</strong> {selected.project_title}</p>
              <p><strong>Date:</strong> {selected.date}</p>
              <p><strong>Check-In:</strong> {selected.check_in}</p>
              <p><strong>Check-Out:</strong> {selected.check_out}</p>
              <p><strong>Worked Hours:</strong> {selected.working_hours} hrs</p>
              <p><strong>Overtime:</strong> {selected.overtime}</p>
              <p className="col-span-2">
                <strong>Status:</strong>{" "}
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    selected.status === "Approved"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {selected.status}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Add Timesheet Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-4xl relative overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-semibold text-gray-800">Add Timesheets</h3>
              <button
                onClick={() => setAddModal(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="space-y-4">
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Name
                  </label>
                  <select
                    name="emp_id"
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees
                      .filter(
                        (emp) =>
                          emp.status?.toString().trim().toLowerCase() === "active"
                      )
                      .map((emp) => (
                        <option key={emp.empId} value={emp.empId}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name
                  </label>
                  <select
                    name="project_id"
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((pro) => (
                      <option key={pro.id} value={pro.id}>
                        {pro.project_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Worked Hours
                  </label>
                  <input
                    type="text"
                    name="working_hours"
                    value={addForm.working_hours}
                    readOnly
                    className="border p-2 w-full bg-gray-100 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="check_in"
                    className="border p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                    onChange={(e) => {
                      const check_in = e.target.value;
                      const check_out = addForm.check_out;

                      const working_hours = calculateHours(check_in, check_out);

                      setAddForm((prev) => ({
                        ...prev,
                        check_in,
                        working_hours,
                      }));
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="check_out"
                    className="border p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                    onChange={(e) => {
                      const check_out = e.target.value;
                      const check_in = addForm.check_in;

                      const working_hours = calculateHours(check_in, check_out);

                      setAddForm((prev) => ({
                        ...prev,
                        check_out,
                        working_hours,
                      }));
                    }}
                  />
                </div>
              </div>

              {/* Row 4 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Overtime
                  </label>
                  <input
                    type="text"
                    name="overtime"
                    placeholder="e.g., 1.5 hrs"
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    defaultValue="Pending"
                  >
                    <option value="Approved">Approved</option>
                    <option value="Pending">Pending</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setAddModal(false)}
                  className="border border-gray-300 px-5 py-2 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className=" text-white px-6 py-2 rounded-lg "
                  style={{
                    background: "linear-gradient(135deg, #2563eb, #d12465)",
                  }}
                >
                  Add Timesheet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Edit Timesheet Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-4xl relative overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-semibold text-gray-800">Edit Timesheet</h3>
              <button
                onClick={() => setEditModal(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Name
                  </label>
                  {/*
                    FIX: previously this list was filtered to Active employees only.
                    If the employee assigned to this timesheet had since been
                    deactivated, they were missing from the <option> list, so
                    defaultValue had nothing to match and it fell back to
                    "Select Employee". We now always include the currently
                    assigned employee even if inactive, and key the select on
                    the record id so it remounts cleanly for each edit.
                  */}
                  <select
                    key={editing?.id}
                    name="emp_id"
                    defaultValue={editing?.emp_id?.toString() || ""}
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees
                      .filter(
                        (emp) =>
                          emp.status === "Active" ||
                          emp.empId?.toString() === editing?.emp_id?.toString()
                      )
                      .map((emp) => (
                        <option key={emp.empId} value={emp.empId.toString()}>
                          {emp.first_name} {emp.last_name}
                          {emp.status !== "Active" ? " (Inactive)" : ""}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Project Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project
                  </label>
                  <select
                    key={editing?.id}
                    name="project_id"
                    defaultValue={editing?.project_id?.toString() || ""}
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((proj) => (
                      <option key={proj.id} value={proj.id.toString()}>
                        {proj.project_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    defaultValue={
                      editing?.date
                        ? editing.date.split("-").reverse().join("-") // DD-MM-YYYY → YYYY-MM-DD
                        : ""
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Worked Hours
                  </label>
                  <input
                    type="text"
                    value={editForm.working_hours}
                    readOnly
                    className="border border-gray-300 rounded-lg p-2 w-full bg-gray-100"
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Check-In
                  </label>
                  <input
                    type="time"
                    name="check_in"
                    value={editForm.check_in}
                    onChange={(e) => {
                      const check_in = e.target.value;
                      const check_out = editForm.check_out;

                      const working_hours = calculateHours(check_in, check_out);

                      setEditForm((prev) => ({
                        ...prev,
                        check_in,
                        working_hours,
                      }));
                    }}
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Check-Out
                  </label>
                  <input
                    type="time"
                    name="check_out"
                    value={editForm.check_out}
                    onChange={(e) => {
                      const check_out = e.target.value;
                      const check_in = editForm.check_in;

                      const working_hours = calculateHours(check_in, check_out);

                      setEditForm((prev) => ({
                        ...prev,
                        check_out,
                        working_hours,
                      }));
                    }}
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    required
                  />
                </div>
              </div>

              {/* Row 4 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Overtime
                  </label>
                  <input
                    type="text"
                    name="overtime"
                    defaultValue={editing?.overtime}
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    defaultValue={editing?.status || "Pending"}
                    className="border border-gray-300 rounded-lg p-2 w-full outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                  >
                    <option value="Approved">Approved</option>
                    <option value="Pending">Pending</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setEditModal(false)}
                  className="border border-gray-300 px-5 py-2 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}